import { ArrowRight } from 'lucide-react';
import { usePostHog } from 'posthog-js/react';
import type React from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { useAppContext } from '@/app/providers/AppContextProvider';
import PageStrip from '@/components/layout/PageStrip';
import Spinner from '@/components/shared/Spinner';
import { Button } from '@/components/ui/button';
import { useAppStatePolling } from '@/hooks/useAppStatePolling';
import { useFlowHighlightClass } from '@/hooks/useFlowGuidance';
import { hasAddOnProducts } from '@/lib/addOns';
import { cn } from '@/lib/utils';
import { SupportedProductType } from '@/types/api';
import WgaExtended from './ProductSelection/components/sections/Insurance/WgaExtended';
import WiaExcess from './ProductSelection/components/sections/Insurance/WiaExcess';
import OccupationalHealthService from './ProductSelection/components/sections/OccupationalHealthService';
import Summary from './ProductSelection/components/sections/Summary';

type AddonSectionKey = 'occupationalHealthService' | 'wgaExtended' | 'wiaExcess';

interface AddonSectionProps {
	showNavigation: false;
	onLoadingChange: (isLoading: boolean) => void;
	onItemCountChange: (itemCount: number) => void;
	desktopVisibleCards: number;
}

const DEFAULT_DESKTOP_VISIBLE_CARDS = 4;
const EXPANDED_DESKTOP_VISIBLE_CARDS = 5;
const EXPANDED_DESKTOP_THRESHOLD = 4;

const createAddonRecord = <TValue,>(value: TValue) => ({
	occupationalHealthService: value,
	wgaExtended: value,
	wiaExcess: value
});

const ProductSelection = (): React.JSX.Element | null => {
	const { t } = useTranslation();
	const posthog = usePostHog();
	// Flow guidance points here on the add-ons step: highlight the proceed button.
	const proceedHighlight = useFlowHighlightClass('proceed-addons');
	const { id, selected } = useParams<{ id: string; selected: string }>();
	const navigate = useNavigate();
	const { applicationData, applicationManagement, appData } = useAppContext();
	const [isVisible, setIsVisible] = useState(false);
	const [isSaving, setIsSaving] = useState(false);
	const [isFetchingData, setIsFetchingData] = useState(!!id);
	const [fetchError, setFetchError] = useState<string | null>(null);
	const [addonLoadingState, setAddonLoadingState] = useState<Record<AddonSectionKey, boolean>>(
		createAddonRecord(false)
	);
	const [addonItemCounts, setAddonItemCounts] = useState<Record<AddonSectionKey, number>>(
		createAddonRecord(0)
	);

	const relevantProductTypes = useMemo<SupportedProductType[]>(() => {
		const raw = appData?.relevant_product_types;
		if (!Array.isArray(raw)) return [];
		const allowed = new Set<string>(Object.values(SupportedProductType));
		return raw.filter((value): value is SupportedProductType => allowed.has(value));
	}, [appData?.relevant_product_types]);
	const latestApplicationDataRef = useRef(applicationData);
	const hasOccupationalHealthService = relevantProductTypes.includes(
		SupportedProductType.OCCUPATIONAL_HEALTH_SERVICE
	);
	const hasWgaExtended = relevantProductTypes.includes(
		SupportedProductType.SICK_LEAVE_LONG_TERM_WGA_GAP_EXTENDED
	);
	const hasWiaExcess = relevantProductTypes.includes(
		SupportedProductType.SICK_LEAVE_LONG_TERM_WIA_EXCESS
	);
	const hasAddOns = hasAddOnProducts(relevantProductTypes);
	const addonAvailability: Record<AddonSectionKey, boolean> = {
		occupationalHealthService: hasOccupationalHealthService,
		wgaExtended: hasWgaExtended,
		wiaExcess: hasWiaExcess
	};

	const fetchApplicationData = applicationManagement.fetch;
	const saveApplication = applicationManagement.save;
	const updateApplication = applicationManagement.update;
	const hasFetchedRef = useRef<string | null>(null);
	const persistedSelectionRef = useRef<string | null>(null);

	useAppStatePolling({ id, enabled: !!id });

	useEffect(() => {
		latestApplicationDataRef.current = applicationData;
	}, [applicationData]);

	useEffect(() => {
		if (id) posthog?.identify(id);
	}, [id, posthog]);

	useEffect(() => {
		if (!id) {
			setIsFetchingData(false);
			return;
		}
		// Claim the fetch synchronously so a remount (React StrictMode double-invoke,
		// fast route re-entry) can't fire a second identical GET before the first
		// resolves. Reset on failure so the retry button can try again.
		if (hasFetchedRef.current === id) return;
		hasFetchedRef.current = id;
		setIsFetchingData(true);
		setFetchError(null);
		fetchApplicationData(id)
			.catch((error) => {
				hasFetchedRef.current = null;
				const message = error instanceof Error ? error.message : String(error);
				setFetchError(message);
			})
			.finally(() => {
				setIsFetchingData(false);
			});
	}, [id, fetchApplicationData]);

	useEffect(() => {
		if (!id || !selected) return;
		latestApplicationDataRef.current = {
			...latestApplicationDataRef.current,
			selected_sick_leave_short_term_quote_id: selected
		};
		updateApplication({ selected_sick_leave_short_term_quote_id: selected });
	}, [id, selected, updateApplication]);

	// Persist the sick-leave selection as soon as the applicant lands here, not only
	// when they proceed to the overview. Otherwise the "you selected …" summary shows
	// but the choice is never stored, so advisors don't see it in the dossier if the
	// applicant stops on this step. Runs once the initial fetch is done so the GET's
	// (possibly null) selection field can't clobber our PATCH; keyed by `selected` so
	// re-picking a different quote re-saves; resets on failure to allow a retry.
	useEffect(() => {
		if (!id || !selected || isFetchingData || fetchError) return;
		if (persistedSelectionRef.current === selected) return;
		persistedSelectionRef.current = selected;
		saveApplication(id, { selected_sick_leave_short_term_quote_id: selected }).catch((err) => {
			console.error('Error saving sick-leave quote selection:', err);
			persistedSelectionRef.current = null;
		});
	}, [id, selected, isFetchingData, fetchError, saveApplication]);

	useEffect(() => {
		const timer = setTimeout(() => setIsVisible(true), 100);
		return () => clearTimeout(timer);
	}, []);

	const retryFetch = useCallback(() => {
		if (!id) return;
		hasFetchedRef.current = id;
		setFetchError(null);
		setIsFetchingData(true);
		fetchApplicationData(id)
			.catch((error) => {
				hasFetchedRef.current = null;
				const message = error instanceof Error ? error.message : String(error);
				setFetchError(message);
			})
			.finally(() => {
				setIsFetchingData(false);
			});
	}, [id, fetchApplicationData]);

	const handleAddonLoadingChange = useCallback((key: AddonSectionKey, isLoading: boolean) => {
		setAddonLoadingState((currentState) => {
			if (currentState[key] === isLoading) return currentState;
			return { ...currentState, [key]: isLoading };
		});
	}, []);

	const handleAddonItemCountChange = useCallback((key: AddonSectionKey, itemCount: number) => {
		setAddonItemCounts((currentCounts) => {
			if (currentCounts[key] === itemCount) return currentCounts;
			return { ...currentCounts, [key]: itemCount };
		});
	}, []);

	const areAddonCardsLoading = Object.values(addonLoadingState).some(Boolean);
	const desktopVisibleCards = Object.entries(addonItemCounts).some(
		([key, itemCount]) =>
			addonAvailability[key as AddonSectionKey] && itemCount >= EXPANDED_DESKTOP_THRESHOLD
	)
		? EXPANDED_DESKTOP_VISIBLE_CARDS
		: DEFAULT_DESKTOP_VISIBLE_CARDS;
	const addonSectionProps = useMemo<Record<AddonSectionKey, AddonSectionProps>>(
		() => ({
			occupationalHealthService: {
				showNavigation: false,
				onLoadingChange: (isLoading) =>
					handleAddonLoadingChange('occupationalHealthService', isLoading),
				onItemCountChange: (itemCount) =>
					handleAddonItemCountChange('occupationalHealthService', itemCount),
				desktopVisibleCards
			},
			wgaExtended: {
				showNavigation: false,
				onLoadingChange: (isLoading) => handleAddonLoadingChange('wgaExtended', isLoading),
				onItemCountChange: (itemCount) => handleAddonItemCountChange('wgaExtended', itemCount),
				desktopVisibleCards
			},
			wiaExcess: {
				showNavigation: false,
				onLoadingChange: (isLoading) => handleAddonLoadingChange('wiaExcess', isLoading),
				onItemCountChange: (itemCount) => handleAddonItemCountChange('wiaExcess', itemCount),
				desktopVisibleCards
			}
		}),
		[desktopVisibleCards, handleAddonItemCountChange, handleAddonLoadingChange]
	);

	/** Saves the sick-leave quote selection and advances to the overview. */
	const handleProceedToOverview = useCallback(async (): Promise<void> => {
		if (!id || !selected || areAddonCardsLoading) return;
		setIsSaving(true);
		try {
			const updatedApplicationData = {
				...latestApplicationDataRef.current,
				selected_sick_leave_short_term_quote_id: selected
			};
			await saveApplication(id, updatedApplicationData);
			latestApplicationDataRef.current = updatedApplicationData;
			updateApplication({ selected_sick_leave_short_term_quote_id: selected });
			navigate(`/overzicht/${id}/${selected}`);
		} catch (err) {
			console.error('Error saving product selection:', err);
		} finally {
			setIsSaving(false);
		}
	}, [id, selected, areAddonCardsLoading, saveApplication, updateApplication, navigate]);

	if (!id || !selected) return null;

	if (isFetchingData) {
		return (
			<PageStrip className="py-4 lg:py-14" contentClassName="mt-8 sm:mt-16 flex justify-center">
				<Spinner size="lg" visible />
			</PageStrip>
		);
	}

	if (fetchError) {
		return (
			<PageStrip className="py-4 lg:py-14" contentClassName="mt-8 sm:mt-16 text-center">
				<p className="text-destructive mb-4">{t('application.fetchError')}</p>
				<Button onClick={retryFetch}>{t('common.retry')}</Button>
			</PageStrip>
		);
	}

	return (
		<div
			className={cn(
				'transition-opacity duration-300',
				isVisible ? 'opacity-100' : 'opacity-0',
				isSaving && 'opacity-50 pointer-events-none'
			)}
		>
			<PageStrip
				className="bg-neutral-100 pt-4 sm:pt-5 lg:pt-8 pb-4 lg:pb-14"
				contentClassName="mt-4 sm:mt-6"
			>
				<Summary id={id} selected={selected} />
			</PageStrip>

			{hasAddOns && (
				<PageStrip
					testId="summary-addons-divider"
					className="border-t border-border/70 pt-14 lg:pt-20 pb-2 lg:pb-4"
				>
					<header className="px-2 mb-10">
						<h2
							data-testid="addons-title"
							className="text-left text-[1.6875rem] md:text-4xl mb-4 leading-[1.1] font-bold tracking-tight text-balance"
						>
							{t('productSelection.addons.title')}
						</h2>
						<p className="text-left text-base md:text-lg font-bold text-[var(--color-primary-title,var(--color-primary))] max-w-2xl leading-relaxed">
							{t('productSelection.addons.subtitle')}
						</p>
					</header>

					<div className="flex flex-col gap-12">
						<hr className="border-border" />

						{hasOccupationalHealthService && (
							<OccupationalHealthService id={id} {...addonSectionProps.occupationalHealthService} />
						)}

						{hasWgaExtended && (
							<>
								{hasOccupationalHealthService && <hr className="border-border" />}
								<WgaExtended id={id} {...addonSectionProps.wgaExtended} />
							</>
						)}

						{hasWiaExcess && (
							<>
								{(hasOccupationalHealthService || hasWgaExtended) && (
									<hr className="border-border" />
								)}
								<WiaExcess id={id} {...addonSectionProps.wiaExcess} />
							</>
						)}
					</div>
				</PageStrip>
			)}

			<PageStrip
				className="pt-4 lg:pt-6 pb-20 lg:pb-24"
				contentClassName="flex flex-col-reverse sm:flex-row sm:justify-center gap-2 sm:gap-0"
			>
				<Button
					type="button"
					variant="outline"
					size="xl"
					className="font-bold w-full sm:w-auto sm:mr-3 lg:mr-4"
					onClick={() => navigate(`/aanbod/${id}`)}
					disabled={isSaving || areAddonCardsLoading}
					data-testid="application-previous-button"
				>
					{t('application.navigation.previous')}
				</Button>
				<Button
					type="button"
					variant="default"
					size="xl"
					className={cn('font-bold w-full sm:w-auto', proceedHighlight)}
					onClick={handleProceedToOverview}
					disabled={isSaving || areAddonCardsLoading}
					loading={isSaving}
					iconRight={<ArrowRight className="w-4 h-4" />}
					data-testid="application-next-button"
				>
					{t('application.steps.step1.nextButton')}
				</Button>
			</PageStrip>
		</div>
	);
};

export default ProductSelection;
